import { useApolloClient } from '@apollo/client'
import {
  LastSyncRunDocument,
  MarketplaceDbIntegration,
  SyncRunStatusChangeDocument,
  SyncRunsParametersInput,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'

interface UseSyncRunStatusChangedSubscriptionProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
  parameters: SyncRunsParametersInput[]
}

export const useSyncRunStatusChangedSubscription = ({
  marketplaceIntegration,
  syncEnabled,
  parameters,
}: UseSyncRunStatusChangedSubscriptionProps) => {
  const client = useApolloClient()

  useSubscription(SyncRunStatusChangeDocument, {
    onData: ({ data: subscriptionData }) => {
      const syncRun = subscriptionData.data?.integrationsSyncRunStatusChange?.syncRun
      if (!syncRun || !marketplaceIntegration?.id) return

      // TODO: API&Marketplace will improve this to support multiple parameters and agnostic to context.
      // We'll try to decouple this part using something like an "CapabilityObject". This object will expose
      // an interface with filterKeys and a set of params to apply filters similar to those lines, but context-agnostic.
      const contextPayrollRunId = parameters.find((p) => p.key === 'payroll_run_id')?.value
      const syncRunPayrollRunId = syncRun.parameters?.find((p) => p.key === 'payroll_run_id')?.value
      if (contextPayrollRunId && contextPayrollRunId !== syncRunPayrollRunId) {
        return
      }

      client.writeQuery({
        query: LastSyncRunDocument,
        variables: { marketplaceIntegrationId: marketplaceIntegration.id, parameters },
        data: {
          integrations: {
            syncRunsConnection: {
              nodes: [
                {
                  id: syncRun.id,
                  finishedAt: syncRun.finishedAt,
                  status: syncRun.status,
                  parameters: syncRun.parameters.map((param) => ({
                    key: param.key,
                    value: param.value,
                    __typename: 'IntegrationsSyncRunParameters' as const,
                  })),
                  __typename: 'IntegrationsSyncRun',
                },
              ],
              __typename: 'IntegrationsSyncRunConnection',
            },
            __typename: 'integrations',
          },
        },
      })
    },
    skip: !syncEnabled,
  })
}
